import { HARDWARES_API_URL, HARDWARES_UNASSIGNED_URL } from 'constants/ApiConstants';
import type { ApiError, MetaAction } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';
import * as types from 'modules/hardwares/actionTypes';
import type * as HardwareTypes from 'modules/hardwares/types';

/**
 * Clear all filter fields in the Hardwares dashboard.
 */
export const clearHardwareDashboardFilters = (
  meta: MetaAction = {}
): HardwareTypes.ClearHardwareDashboardFiltersAction => ({
  type: types.CLEAR_HARDWARES_DASHBOARD_FILTER,
  meta,
});

/**
 * Set a single filter field in the Hardwares dashboard.
 */
export const setHardwareDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {}
): HardwareTypes.SetHardwareDashboardFilterAction => ({
  type: types.SET_HARDWARES_DASHBOARD_FILTER,
  payload: { field, value },
  meta,
});

/**
 * Remove a single filter field in the Hardwares dashboard.
 */
export const removeHardwareDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {}
): HardwareTypes.RemoveHardwareDashboardFilterAction => ({
  type: types.REMOVE_HARDWARES_DASHBOARD_FILTER,
  payload: {
    field,
    value,
  },
  meta,
});

/**
 * Open the creation/edit modal for a Hardware.
 */
export const openHardwareCreateModal = (
  hardwareId: MayBe<string>,
  meta: MetaAction = {}
): HardwareTypes.OpenHardwareCreateModalAction => ({
  type: types.OPEN_CREATE_HARDWARE_MODAL,
  payload: hardwareId,
  meta,
});

/**
 * Hide the creation/edit modal for a Hardware.
 */
export const hideHardwareCreateModal = (meta: MetaAction = {}): HardwareTypes.HideHardwareCreateModalAction => ({
  type: types.HIDE_CREATE_HARDWARE_MODAL,
  meta,
});

/**
 * Fetch all Hardwares from the API.
 */
export const fetchHardwares = (
  params: HardwareTypes.HardwaresQueryParams,
  meta: MetaAction = {}
): HardwareTypes.FetchHardwaresAction => ({
  type: types.FETCH_HARDWARES_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: HARDWARES_API_URL,
      params,
    },
  },
  meta,
});

/**
 * Fetch all unassigned Hardwares from the API.
 */
export const fetchUnassignedHardwares = (meta: MetaAction = {}): HardwareTypes.FetchUnassignedHardwaresAction => ({
  type: types.FETCH_UNASSIGNED_HARDWARES_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: HARDWARES_UNASSIGNED_URL,
    },
  },
  meta,
});

/**
 * Unlink (delete) a Hardware from a dealer (a PATCH on `/{id}/unlink`).
 * `hardwareId` is carried in the payload so the success/failure callbacks and the
 * ids reducer can clean the store.
 */
export const deleteHardwareFromDealer = (
  hardwareId: string,
  params?: Record<string, unknown>
): HardwareTypes.DeleteHardwareFromDealerAction => ({
  type: types.DELETE_HARDWARE_REQUEST,
  payload: {
    hardwareId,
    request: {
      method: 'PATCH',
      url: `${HARDWARES_API_URL}/${hardwareId}/unlink`,
      params,
    },
  },
});

/**
 * Dispatched on successful deletion of a Hardware from a dealer.
 */
export const deleteHardwareFromDealerSuccess = (
  hardwareId: string,
  meta: MetaAction = {}
): HardwareTypes.DeleteHardwareFromDealerSuccessAction => ({
  type: types.DELETE_HARDWARE_SUCCESS,
  payload: hardwareId,
  meta,
});

/**
 * Dispatched on failure to delete a Hardware from a dealer.
 */
export const deleteHardwareFromDealerFailure = (
  error: ApiError,
  meta: MetaAction = {}
): HardwareTypes.DeleteHardwareFromDealerFailureAction => ({
  type: types.DELETE_HARDWARE_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * Link (assign) a Hardware to a dealer.
 */
export const linkHardwareToDealer = (
  data: HardwareTypes.LinkHardwareToDealerData,
  meta: MetaAction = {}
): HardwareTypes.LinkHardwareToDealerAction => ({
  type: types.LINK_HARDWARE_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${HARDWARES_API_URL}/${data.hardwareId}/link`,
      data,
    },
  },
  meta,
});

/**
 * Update the status (ACTIVE / INACTIVE) of a Hardware.
 */
export const updateHardwareStatus = (
  data: HardwareTypes.UpdateHardwareStatusData,
  meta: MetaAction = {}
): HardwareTypes.PatchStatusHardwareAction => ({
  type: types.PATCH_STATUS_HARDWARE_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${HARDWARES_API_URL}/${data.hardwareId}/status`,
      data,
    },
  },
  meta,
});
